// ─────────────────────────────────────────────────────────────────────
// <MoodPage /> — /m/:slug
//
// Each mood has its own editorial framing + curated events.
// ─────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import api from '../api/client';
import { useLocation as useCity } from '../context/LocationContext';
import EventCard from '../components/event/EventCard';

const MOOD_META = {
  'this-weekend':   { title: 'This',           accent: 'weekend.',  variant: 'gold',  lede: 'Anything happening Friday–Sunday.' },
  'tonight':        { title: 'For',            accent: 'tonight.',  variant: 'rose',  lede: 'Last-minute plans, settled.' },
  'date-night':     { title: 'A proper',       accent: 'date night.',variant: 'rose', lede: 'Quiet venues, end-of-evening dinners nearby.' },
  'family-day':     { title: 'A whole',        accent: 'family day.',variant: 'sage', lede: 'Kid-friendly, daytime, easy parking.' },
  'quiet-evening':  { title: 'A quiet',        accent: 'evening.',  variant: 'gold',  lede: 'Classical, devotional, intimate venues.' },
  'late-night':     { title: 'After',          accent: 'midnight.', variant: 'plum',  lede: 'Late shows, indie venues, club nights.' },
  'under-500':      { title: 'Showz under',    accent: '₹500.',     variant: 'sage',  lede: 'Affordable picks across genres.' },
  'free':           { title: 'Completely',     accent: 'free.',     variant: 'sage',  lede: 'No tickets needed. Just show up.' },
  'near-me':        { title: 'Right',          accent: 'near you.', variant: 'slate', lede: 'Within a 5km radius of your location.' },
  'indie-and-live': { title: 'Indie',          accent: '& live.',   variant: 'plum',  lede: 'Original work in small rooms.' },
  'classical':      { title: 'Classical',      accent: 'evenings.', variant: 'gold',  lede: 'Carnatic, Hindustani, instrumental.' },
  'devotional':     { title: 'Devotional',     accent: 'gatherings.',variant: 'gold', lede: 'Bhakti, kirtans, satsangs.' },
  // r7.7 D1 — the rest of the 22 curated lists
  'comedy-night':   { title: 'A comedy',       accent: 'night.',    variant: 'rose',  lede: 'Stand-up, improv, open mics.' },
  'big-stage':      { title: 'The big',        accent: 'stage.',    variant: 'gold',  lede: 'Arena shows, headliners, festivals.' },
  'first-timers':   { title: 'Easy for',       accent: 'first-timers.', variant: 'sage', lede: 'Friendly rooms, no prior knowledge needed.' },
  'with-friends':   { title: 'Out with',       accent: 'friends.',  variant: 'plum',  lede: 'Group rooms, split payments, big tables.' },
  'kids':           { title: 'For the',        accent: 'kids.',     variant: 'sage',  lede: 'Puppets, science, story time, workshops.' },
  'learn-something':{ title: 'Learn',          accent: 'something.',variant: 'plum',  lede: 'Workshops, masterclasses, talks.' },
  'outdoors':       { title: 'Out in the',     accent: 'open.',     variant: 'sage',  lede: 'Grounds, lawns, rooftops, trails.' },
  'sports-day':     { title: 'Game',           accent: 'day.',      variant: 'sage',  lede: 'Matches, courts to book, watch parties.' },
  'festival-season':{ title: 'Festival',       accent: 'season.',   variant: 'gold',  lede: 'Multi-day passes, fairs, melas.' },
  'premieres':      { title: 'Opening',        accent: 'nights.',   variant: 'rose',  lede: 'First shows, launches, debuts.' },
};
export const MOODS = Object.keys(MOOD_META).map((slug) => ({ slug, label: `${MOOD_META[slug].title} ${MOOD_META[slug].accent}`.replace(/\.$/, ''), variant: MOOD_META[slug].variant, lede: MOOD_META[slug].lede }));

const DEFAULT = { title: 'A curated', accent: 'experience.', variant: 'gold', lede: 'Hand-picked events for this mood.' };

export default function MoodPage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { city } = useCity();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const meta = MOOD_META[slug] || DEFAULT;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get('/events/by-mood', {
          params: { mood: slug, city, limit: 24 },
        });
        if (!cancelled) setEvents(data.events || []);
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, city]);

  return (
    <div>
      <section className={`snt-art-${meta.variant}`}>
        <div className="snt-container py-20 lg:py-28">
          <div className="snt-eyebrow mb-5">{t('mood.eyebrow', 'Curated mood')}</div>
          <h1
            className="font-serif font-light mb-5"
            style={{
              fontSize: 'clamp(48px, 9vw, 110px)',
              lineHeight: 0.92,
              letterSpacing: '-0.035em',
              color: 'var(--snt-text)',
              maxWidth: '8em',
            }}
          >
            {meta.title}{' '}
            <span className="snt-em-accent">{meta.accent}</span>
          </h1>
          <p className="text-[17px] leading-relaxed max-w-[480px]" style={{ color: 'var(--snt-text-2)' }}>
            {meta.lede}
          </p>
        </div>
      </section>

      <section className="snt-container py-10 lg:py-14">
        {loading ? (
          <div className="text-[13px]" style={{ color: 'var(--snt-text-3)' }}>
            {t('common.loading', 'Loading…')}
          </div>
        ) : events.length === 0 ? (
          <div className="py-16 text-center">
            <h3 className="font-serif text-[26px] mb-2" style={{ color: 'var(--snt-text)' }}>
              {t('mood.empty_title', 'Nothing yet for this mood')}
            </h3>
            <p className="text-[13px]" style={{ color: 'var(--snt-text-3)' }}>
              {t('mood.empty_hint', 'Curators are working on it. Check back tomorrow.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
            {events.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
